import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PartySelector } from "@/app/components/common/PartySelector";
import { createVehicle, getVehicleByRegistration } from "@/app/services/vehicleService";
import { VehicleCreate } from "@/app/types/vehicle";

const formSchema = z.object({
  registration_number: z
    .string()
    .min(1, "Registration number is required")
    .max(20, "Registration number must be 20 characters or less"),
  make: z.string().optional().nullable(),
  model: z.string().optional().nullable(),
  owner_id: z.number().optional().nullable(),
  fuel_type: z.enum(['petrol', 'diesel', 'cng', 'electric', 'hybrid']).optional().nullable(),
  year_of_manufacture: z.coerce.number().min(1950).max(2030).optional().nullable(),
});

type QuickVehicleFormValues = z.infer<typeof formSchema>;

interface QuickVehicleFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (vehicleId: number, vehicle: any) => void;
}

const QuickVehicleForm: React.FC<QuickVehicleFormProps> = ({
  open,
  onOpenChange,
  onCreated,
}) => {
  const [existingVehicle, setExistingVehicle] = useState<any>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  const form = useForm<QuickVehicleFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      registration_number: "",
      make: "",
      model: "",
      owner_id: null,
      fuel_type: null,
      year_of_manufacture: null,
    },
  });

  React.useEffect(() => {
    if (open) {
      setExistingVehicle(null);
      setIsLookingUp(false);
    }
  }, [open]);

  const handleRegistrationBlur = async () => {
    const regNumber = form.getValues('registration_number')?.trim();
    if (!regNumber || regNumber.length < 3) {
      setExistingVehicle(null);
      return;
    }
    setIsLookingUp(true);
    try {
      const vehicle = await getVehicleByRegistration(regNumber.toUpperCase());
      setExistingVehicle(vehicle);
    } catch {
      // 404 = vehicle doesn't exist, which is fine
      setExistingVehicle(null);
    } finally {
      setIsLookingUp(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: (data: VehicleCreate) => createVehicle(data),
    onSuccess: (newVehicle) => {
      toast.success(
        `Vehicle "${newVehicle.registration_number}" registered successfully!`
      );
      onCreated(newVehicle.id, newVehicle);
      form.reset();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      let errorMsg = error.message || "Unknown error";
      if (error instanceof AxiosError && error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      toast.error(`Failed to register vehicle: ${errorMsg}`);
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    const payload: VehicleCreate = {
      registration_number: data.registration_number.toUpperCase(),
      make: data.make || null,
      model: data.model || null,
      owner_id: data.owner_id ?? null,
      fuel_type: data.fuel_type || null,
      year_of_manufacture: data.year_of_manufacture || null,
    };
    createMutation.mutate(payload);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Quick Vehicle Registration</DialogTitle>
          <DialogDescription>
            Register a new vehicle with basic details.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="registration_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Registration Number*</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. TN01AB1234"
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase())
                      }
                      onBlur={handleRegistrationBlur}
                      maxLength={20}
                    />
                  </FormControl>
                  <FormMessage />
                  {isLookingUp && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> Checking registration...
                    </p>
                  )}
                </FormItem>
              )}
            />

            {existingVehicle && (
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="flex items-center justify-between">
                  <span className="text-sm">
                    Vehicle <strong>{existingVehicle.registration_number}</strong> is already registered
                    {existingVehicle.make && ` (${existingVehicle.make} ${existingVehicle.model || ''})`}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      onCreated(existingVehicle.id, existingVehicle);
                      form.reset();
                      setExistingVehicle(null);
                      onOpenChange(false);
                    }}
                  >
                    Use Existing
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="make"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Make</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        placeholder="e.g. Toyota"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Model</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        placeholder="e.g. Innova"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fuel_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fuel Type</FormLabel>
                    <Select
                      onValueChange={(val) => field.onChange(val === "__none__" ? null : val)}
                      value={field.value || "__none__"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select fuel type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">Not specified</SelectItem>
                        <SelectItem value="petrol">Petrol</SelectItem>
                        <SelectItem value="diesel">Diesel</SelectItem>
                        <SelectItem value="cng">CNG</SelectItem>
                        <SelectItem value="electric">Electric</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="year_of_manufacture"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Year</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="e.g. 2020"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="owner_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Owner</FormLabel>
                  <PartySelector
                    value={field.value ?? undefined}
                    onChange={(value) => field.onChange(value ?? null)}
                    placeholder="Select owner..."
                    filterPartyType="customer"
                    disabled={createMutation.isPending}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Registering...
                  </>
                ) : (
                  "Register Vehicle"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickVehicleForm;
